import { useCallback } from "react";
import { useAnalytics } from "./useAnalytics";

type CTALocation = 
  | "hero" 
  | "demo" 
  | "pricing" 
  | "pricing_card"
  | "footer" 
  | "cta_section"
  | "nav"
  | "nav_mobile";

type CTAType = 
  | "start_free" 
  | "watch_demo" 
  | "view_pricing" 
  | "contact_sales"
  | "newsletter"
  | "login"
  | "choose_plan";

export const useLandingTracking = () => {
  const { trackEvent } = useAnalytics();

  // Track CTA clicks
  const trackCTA = useCallback((type: CTAType, location: CTALocation) => {
    trackEvent({
      category: "Landing",
      action: "cta_click",
      label: `${type}_${location}`,
    });
  }, [trackEvent]);

  // Track demo video play
  const trackVideoPlay = useCallback(() => {
    trackEvent({
      category: "Landing",
      action: "video_play",
      label: "demo_video",
    });
  }, [trackEvent]);

  // Track FAQ opens
  const trackFAQOpen = useCallback((questionIndex: number) => {
    trackEvent({
      category: "Landing",
      action: "faq_open",
      label: `question_${questionIndex}`,
    });
  }, [trackEvent]);

  // Track newsletter signup
  const trackNewsletterSubmit = useCallback((location: string = "footer") => {
    trackEvent({
      category: "Landing",
      action: "newsletter_submit",
      label: location,
    });
  }, [trackEvent]);

  // Track testimonial interaction
  const trackTestimonialView = useCallback(() => {
    trackEvent({
      category: "Landing",
      action: "testimonial_view",
      label: "stats_bar",
    });
  }, [trackEvent]);

  // Track scroll depth
  const trackScrollDepth = useCallback((depth: number) => {
    trackEvent({
      category: "Landing",
      action: "scroll_depth",
      label: `${depth}%`,
      value: depth,
    });
  }, [trackEvent]);

  // Track feature click
  const trackFeatureClick = useCallback((featureName: string) => {
    trackEvent({
      category: "Landing",
      action: "feature_click",
      label: featureName,
    });
  }, [trackEvent]);

  // Track integration click
  const trackIntegrationClick = useCallback((integrationName: string) => {
    trackEvent({
      category: "Landing",
      action: "integration_click",
      label: integrationName,
    });
  }, [trackEvent]);

  // Track pricing plan view
  const trackPricingPlanView = useCallback((planName: string) => {
    trackEvent({
      category: "Landing",
      action: "pricing_plan_view",
      label: planName,
    });
  }, [trackEvent]);

  return {
    trackCTA,
    trackVideoPlay,
    trackFAQOpen,
    trackNewsletterSubmit,
    trackTestimonialView,
    trackScrollDepth,
    trackFeatureClick,
    trackIntegrationClick,
    trackPricingPlanView,
  };
};
